import React, { forwardRef } from 'react';
import { bool, element, oneOfType, oneOf, func, string } from 'prop-types';
import classNames from 'classnames/bind';
import * as styles from './radio.css';
import { dt } from '../../helpers/Utils/GetTailwindToken';

const cx = classNames.bind(styles);

/**
 * Displays a Radio Button control.
 *
 * All extra `props` (not accounted for in `propTypes`) get spread on the `<input/>` element, including `ref`.
 */
const Radio = forwardRef((props, ref) => {
    const {
        automationId,
        id,
        className,
        defaultChecked,
        disabled,
        error,
        label,
        name,
        value,
        labelStyle,
        labelclassName,
        size,
        onClick,
        onChange,
        dataQmBlock,
        dataQmEncrypt,
        datatlPrivate,
        autoComplete,
        inputKey,
        wrapperTheme,
        ...attrs
    } = props;

    const wrapperClasses = cx(className, 'radio', {
        error,
    });

    const lineheightStyle = size !== 'Sm' ? dt(['leading-6']) : dt(['leading-5']);

    const wrapperBlock = dt(['relative', 'select-none', 'inline-block', 'font-open-sans-regular']);
    const inputBlock = `${dt(['pointer-events-none', 'opacity-0', 'hidden'])} ${cx('radioInput')}`;

    const labelBaseBlock = dt([
        'cursor-pointer',
        'text-black',
        'inline-block',
        'before:left-0',
        'before:top-0',
        `before:content-['']`,
        'before:block',
        'before:cursor-pointer',
        'before:border',
        'before:absolute',
        'before:border-solid',
        'before:rounded-half',
        'before:border-gray-60',
        'before:bg-white',
        `after:content-['']`,
        'after:top-1',
        'after:left-1',
        'after:border-0',
        'after:block',
        'after:cursor-pointer',
        'after:absolute',
        'after:rounded-half',
    ]);

    const sizeLabelStyle =
        size === 'Sm'
            ? dt([
                  'before:w-5',
                  'before:h-5',
                  'after:w-3',
                  'after:h-3',
                  'text-small',
                  'pl-8',
                  'min-h-[20px]',
              ])
            : dt([
                  'before:w-6',
                  'before:h-6',
                  'after:w-4',
                  'after:h-4',
                  'text-normal',
                  'pl-9',
                  'min-h-[24px]',
              ]);

    return (
        <div className={`${wrapperClasses} ${wrapperBlock} ${lineheightStyle} ${wrapperTheme}`}>
            <input
                id={id}
                ref={ref}
                name={name}
                type="radio"
                value={value}
                disabled={disabled}
                defaultChecked={defaultChecked}
                className={inputBlock}
                onClick={onClick}
                onChange={onChange}
                data-tlselectprivate={datatlPrivate}
                data-qm-block={dataQmBlock}
                data-qm-encrypt={dataQmEncrypt}
                data-automation-id={automationId}
                autoComplete={autoComplete}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...attrs}
            />
            <label
                htmlFor={id}
                className={`${labelBaseBlock} ${sizeLabelStyle} ${labelStyle} ${labelclassName}`}
                data-qm-block={dataQmBlock}
                data-qm-encrypt={dataQmEncrypt}
            >
                {label}
            </label>
        </div>
    );
});

Radio.propTypes = {
    /** Value for `id` attribute on the `<input/>` and `for` attribute on the `<label>`  */
    id: string.isRequired,

    /** Text or `element` for the `<label>`. Use an `element` when custom styling/markup is required. */
    label: oneOfType([string, element]).isRequired,

    /** Value for `name` attribute on the `<input/>`. */
    name: string.isRequired,

    /** Value for `value` attribute on the `<input/>`. */
    value: string.isRequired,

    /** Apply a `data-automation-id` attribute to the `<input/>`. */
    automationId: string,

    /** Add classes to the component wrapper. */
    className: string,

    /** Set the default `checked` state of the `<input/>`. */
    defaultChecked: bool,

    /** Set the `disabled` state of the `<input/>`. */
    disabled: bool,

    /** Show the `<input/>` as invalid. */
    error: bool,

    /** Size of the radio button * */
    size: oneOf(['Sm', 'Lg']),

    onClick: func,
    onChange: func,
    dataQmBlock: bool,
    dataQmEncrypt: bool,
    datatlPrivate: bool,
    autoComplete: string,
    inputKey: string,
    wrapperTheme: string,
};

Radio.defaultProps = {
    automationId: null,
    className: null,
    defaultChecked: false,
    disabled: false,
    error: false,
    size: 'Lg',
    onClick: () => {},
    onChange: () => {},
    datatlPrivate: false,
    dataQmBlock: false,
    dataQmEncrypt: false,
    autoComplete: 'off',
    wrapperTheme: '',
    inputKey: '',
};

export default Radio;
